import React, { createContext, useState, useEffect, ReactNode, useContext } from 'react';
import { loadFirebaseSDK, FirebaseSDKs } from '../utils/firebaseLazy';
import { Capacitor } from '@capacitor/core';

let sdksInstance: FirebaseSDKs | null = null;

export const initFirebase = async () => {
  if (!sdksInstance) {
    sdksInstance = await loadFirebaseSDK();
  }
  return sdksInstance;
};

export const getFirebaseInstance = () => sdksInstance;

export const auth = new Proxy({} as any, {
  get(_target, prop) {
    if (!sdksInstance) return undefined;
    const value = sdksInstance.auth[prop];
    return typeof value === 'function' ? value.bind(sdksInstance.auth) : value;
  },
  getPrototypeOf() {
    return sdksInstance?.auth ? Object.getPrototypeOf(sdksInstance.auth) : Object.prototype;
  }
});

export const db = new Proxy({} as any, {
  get(_target, prop) {
    if (!sdksInstance) return undefined;
    const value = sdksInstance.db[prop];
    return typeof value === 'function' ? value.bind(sdksInstance.db) : value;
  },
  getPrototypeOf() {
    return sdksInstance?.db ? Object.getPrototypeOf(sdksInstance.db) : Object.prototype;
  }
});

export const storage = new Proxy({} as any, {
  get(_target, prop) {
    if (!sdksInstance) return undefined;
    const value = sdksInstance.storage[prop];
    return typeof value === 'function' ? value.bind(sdksInstance.storage) : value;
  },
  getPrototypeOf() {
    return sdksInstance?.storage ? Object.getPrototypeOf(sdksInstance.storage) : Object.prototype;
  }
});

export const analytics = new Proxy({} as any, {
  get(_target, prop) {
    if (!sdksInstance || !sdksInstance.analytics) return undefined;
    const value = sdksInstance.analytics[prop];
    return typeof value === 'function' ? value.bind(sdksInstance.analytics) : value;
  },
  getPrototypeOf() {
    return sdksInstance?.analytics ? Object.getPrototypeOf(sdksInstance.analytics) : Object.prototype;
  }
});

interface AppUser {
  uid: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
}

interface EmployeeUser {
  id: string;
  managerId: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string;
}

interface AuthResult {
  success: boolean;
  error: string | null;
  verificationSent?: boolean;
  email?: string;
}

interface AuthContextType {
  currentUser: AppUser | null;
  employeeUser: EmployeeUser | null;
  isAuthenticated: boolean;
  isEmployeeAuthenticated: boolean;
  loading: boolean;
  login: (email: string, pass: string, rememberMe: boolean) => Promise<AuthResult>;
  loginAsEmployee: (managerId: string, email: string, pin: string) => Promise<AuthResult>;
  register: (email: string, pass: string, fullName: string, rememberMe: boolean) => Promise<AuthResult>;
  loginWithGoogle: (rememberMe: boolean) => Promise<AuthResult>;
  loginWithApple: (rememberMe: boolean) => Promise<AuthResult>;
  logout: () => void;
  logoutEmployee: () => void;
  updateUserProfile: (data: { displayName?: string, email?: string, photoURL?: string }) => Promise<{ success: boolean; error: string | null }>;
  sendPasswordReset: (email: string) => Promise<AuthResult>;
  deleteUserAccount: () => Promise<{ success: boolean; error: string | null }>;
  resendVerificationEmail: () => Promise<AuthResult>;
  pairKioskDevice: (managerId: string, pin: string, email?: string) => Promise<AuthResult>;
  saveFcmToken: (token: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [employeeUser, setEmployeeUser] = useState<EmployeeUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore Employee Session
    const savedEmployee = localStorage.getItem('kithhr_employee_session');
    if (savedEmployee) {
      try {
        setEmployeeUser(JSON.parse(savedEmployee));
      } catch (e) {
        localStorage.removeItem('kithhr_employee_session');
      }
    }

    // Check if visitor is on a public marketing route and has no active login tokens
    const pathname = window.location.pathname;
    const PUBLIC_MARKETING_ROUTES = [
      '/', '/pricing', '/pricing/', '/about', '/about/',
      '/terms', '/terms/', '/privacy-policy', '/privacy-policy/',
      '/refund-policy', '/refund-policy/', '/resources', '/resources/',
      '/free-hr-software-uk', '/free-hr-software-uk/',
      '/clockify-alternative-uk', '/clockify-alternative-uk/',
      '/pin-time-clock', '/pin-time-clock/'
    ];

    const isPublicRoute = PUBLIC_MARKETING_ROUTES.includes(pathname) || pathname.startsWith('/resources/');
    const hasStoredSession = !!(
      savedEmployee ||
      localStorage.getItem('app_mode') === 'kiosk' ||
      Object.keys(localStorage).some(k => k.startsWith('firebase:authUser'))
    );

    if (isPublicRoute && !hasStoredSession) {
      setLoading(false);
      return;
    }

    let unsubscribe: any = null;
    let isUnmounted = false;

    initFirebase().then(({ auth, db, authSdk, firestoreSdk }) => {
      if (isUnmounted) return;

      const checkRedirectResult = async () => {
        try {
          const result = await authSdk.getRedirectResult(auth);
          if (result && result.user && !result.user.emailVerified) {
            await authSdk.signOut(auth);
            console.error('Your Google account email is not verified.');
          }
        } catch (error: any) {
          console.error("Error getting redirect result:", error);
        }
      };
      
      checkRedirectResult();

      unsubscribe = authSdk.onAuthStateChanged(auth, async (user: any) => {
        try {
          if (user && user.emailVerified) {
            let userData: AppUser = {
              uid: user.uid,
              displayName: user.displayName,
              email: user.email,
              photoURL: user.photoURL,
            };

            try {
              const userDocRef = firestoreSdk.doc(db, 'managers', user.uid);
              const docSnap = await firestoreSdk.getDoc(userDocRef);

              if (docSnap.exists()) {
                const firestoreData = docSnap.data();
                userData = {
                  uid: user.uid,
                  displayName: firestoreData.displayName || user.displayName,
                  email: firestoreData.email || user.email,
                  photoURL: firestoreData.photoURL || user.photoURL,
                };
              } else {
                const newProfileData = {
                  displayName: user.displayName || user.email?.split('@')[0] || 'Manager',
                  email: user.email,
                  photoURL: user.photoURL || null,
                };
                await firestoreSdk.setDoc(userDocRef, newProfileData);
                userData = {
                  uid: user.uid,
                  ...newProfileData,
                };
              }
            } catch (firestoreErr) {
              console.warn("onAuthStateChanged: failed to fetch managers doc, falling back to Firebase Auth user:", firestoreErr);
            }

            setCurrentUser(userData);
          } else if (user && user.isAnonymous) {
            setCurrentUser(null);
          } else {
            setCurrentUser(null);
          }
        } catch (authErr) {
          console.error("onAuthStateChanged error:", authErr);
          setCurrentUser(null);
        } finally {
          setLoading(false);
        }
      });
    });

    return () => {
      isUnmounted = true;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const pairKioskDevice = async (managerId: string, pin: string, email?: string): Promise<AuthResult> => {
    try {
      const { auth, authSdk, functionsSdk, analytics, analyticsSdk } = await initFirebase();
      if (!auth.currentUser) {
        await authSdk.signInAnonymously(auth);
      }

      const functions = functionsSdk.getFunctions(undefined, 'europe-west2');
      const pairAction = functionsSdk.httpsCallable(functions, 'pairKioskDevice');
      const { data } = await pairAction({ managerId, pin, email }) as { data: { success: boolean, role: string } };

      if (data.success) {
        await auth.currentUser?.getIdToken(true);
        if (analytics && analyticsSdk) {
          analyticsSdk.logEvent(analytics, 'kiosk_paired', { managerId, role: data.role });
        }
        return { success: true, error: null };
      }
      return { success: false, error: 'Pairing failed. Please try again.' };
    } catch (error: any) {
      console.error("Pairing Error:", error);
      return { success: false, error: error.message || 'Failed to pair device.' };
    }
  };

  const loginAsEmployee = async (managerId: string, email: string, pin: string): Promise<AuthResult> => {
    try {
      const { db, firestoreSdk, analytics, analyticsSdk } = await initFirebase();
      const { success, error } = await pairKioskDevice(managerId, pin, email);
      if (!success) return { success: false, error };

      const employeesRef = firestoreSdk.collection(db, 'managers', managerId, 'employees');
      const q = firestoreSdk.query(employeesRef, firestoreSdk.where('email', '==', email.toLowerCase().trim()));
      const snap = await firestoreSdk.getDocs(q);

      if (snap.empty) {
        return { success: false, error: 'Employee profile not found after pairing.' };
      }

      const empDoc = snap.docs[0];
      const empData = empDoc.data();

      const employeeObj: EmployeeUser = {
        id: empDoc.id,
        managerId,
        firstName: empData.firstName,
        lastName: empData.lastName,
        email: empData.email,
        avatarUrl: empData.avatarUrl
      };

      setEmployeeUser(employeeObj);
      localStorage.setItem('kithhr_employee_session', JSON.stringify(employeeObj));
      if (analytics && analyticsSdk) {
        analyticsSdk.logEvent(analytics, 'login', { method: 'employee_secure_pair' });
      }
      
      return { success: true, error: null };
    } catch (error: any) {
      console.error("Employee login error:", error);
      return { success: false, error: 'Authentication failed. Please check your connection.' };
    }
  };

  const login = async (email: string, pass: string, rememberMe: boolean): Promise<AuthResult> => {
    try {
      const { auth, authSdk, db, firestoreSdk, analytics, analyticsSdk } = await initFirebase();
      await authSdk.setPersistence(auth, rememberMe ? authSdk.browserLocalPersistence : authSdk.browserSessionPersistence);
      const userCredential = await authSdk.signInWithEmailAndPassword(auth, email, pass);
      const user = userCredential.user;

      if (!user.emailVerified) {
        await authSdk.signOut(auth);
        return { success: false, error: null, verificationSent: true, email: user.email! };
      }
      
      // Clear any stuck employee/kiosk state upon explicit manager login
      localStorage.removeItem('app_mode');
      localStorage.removeItem('kithhr_employee_session');
      setEmployeeUser(null);

      // Immediately set user profile state so isAuthenticated is instantly true
      let userData: AppUser = {
        uid: user.uid,
        displayName: user.displayName || user.email?.split('@')[0] || 'Manager',
        email: user.email,
        photoURL: user.photoURL || null
      };

      try {
        const userDocRef = firestoreSdk.doc(db, 'managers', user.uid);
        const docSnap = await firestoreSdk.getDoc(userDocRef);
        if (docSnap.exists()) {
          const firestoreData = docSnap.data();
          userData = {
            uid: user.uid,
            displayName: firestoreData.displayName || userData.displayName,
            email: firestoreData.email || userData.email,
            photoURL: firestoreData.photoURL || userData.photoURL
          };
        } else {
          const newProfileData = {
            displayName: userData.displayName,
            email: userData.email,
            photoURL: userData.photoURL,
          };
          await firestoreSdk.setDoc(userDocRef, newProfileData);
        }
      } catch (docErr) {
        console.warn("Could not fetch manager profile doc during login, using auth profile:", docErr);
      }

      setCurrentUser(userData);
      setLoading(false);

      if (analytics && analyticsSdk) {
        analyticsSdk.logEvent(analytics, 'login', { method: 'email' });
      }
      return { success: true, error: null };
    } catch (error: any) {
      console.error("Login error:", error);
      let errorMessage = 'An unknown error occurred.';
      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email or password.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        default:
          errorMessage = error.message || 'Failed to sign in. Please try again later.';
          break;
      }
      return { success: false, error: errorMessage };
    }
  };

  const register = async (email: string, pass: string, fullName: string, rememberMe: boolean): Promise<AuthResult> => {
    if (!fullName.trim()) {
        return { success: false, error: 'Please enter your full name.' };
    }
    try {
      const { auth, authSdk, db, firestoreSdk, analytics, analyticsSdk } = await initFirebase();
      await authSdk.setPersistence(auth, rememberMe ? authSdk.browserLocalPersistence : authSdk.browserSessionPersistence);
      const userCredential = await authSdk.createUserWithEmailAndPassword(auth, email, pass);
      const user = userCredential.user;
      
      await authSdk.updateProfile(user, { displayName: fullName });
      
      // Create user document in Firestore
      const userDocRef = firestoreSdk.doc(db, 'managers', user.uid);
      const newProfileData = {
          displayName: fullName,
          email: user.email,
          photoURL: user.photoURL,
      };
      await firestoreSdk.setDoc(userDocRef, newProfileData);

      await authSdk.sendEmailVerification(user);
      
      if (analytics && analyticsSdk) {
        analyticsSdk.logEvent(analytics, 'sign_up', { method: 'email' });
      }
      return { success: true, error: null, verificationSent: true, email: user.email! };
    } catch (error: any) {
      console.error("Registration error:", error);
      let errorMessage = 'An unknown error occurred.';
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'This email address is already in use.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password should be at least 6 characters.';
          break;
        default:
          errorMessage = error.message || 'Failed to create an account. Please try again later.';
          break;
      }
      return { success: false, error: errorMessage };
    }
  };
  
  const loginWithGoogle = async (rememberMe: boolean): Promise<AuthResult> => {
    try {
      const { auth, authSdk, db, firestoreSdk, analytics, analyticsSdk } = await initFirebase();
      await authSdk.setPersistence(auth, rememberMe ? authSdk.browserLocalPersistence : authSdk.browserSessionPersistence);

      let user: any;
      if (Capacitor.isNativePlatform()) {
        const { GoogleSignIn } = await import('@capawesome/capacitor-google-sign-in');
        await GoogleSignIn.initialize({
          clientId: '1084825203431-mbh19cl9tfnl6g6pqd0mff6vv8jlt84i.apps.googleusercontent.com'
        });
        const signInResult = await GoogleSignIn.signIn();
        if (!signInResult?.idToken) {
          throw new Error('No ID token returned from Google Sign-In.');
        }
        const credential = authSdk.GoogleAuthProvider.credential(signInResult.idToken);
        const result = await authSdk.signInWithCredential(auth, credential);
        user = result.user;
      } else {
        const provider = new authSdk.GoogleAuthProvider();
        const result = await authSdk.signInWithPopup(auth, provider);
        user = result.user;
      }
      
      // Clear any stuck employee/kiosk state upon explicit manager login
      localStorage.removeItem('app_mode');
      localStorage.removeItem('kithhr_employee_session');
      setEmployeeUser(null);

      let userData: AppUser = {
        uid: user.uid,
        displayName: user.displayName || user.email?.split('@')[0] || 'Manager',
        email: user.email,
        photoURL: user.photoURL || null
      };

      try {
        const userDocRef = firestoreSdk.doc(db, 'managers', user.uid);
        const docSnap = await firestoreSdk.getDoc(userDocRef);
        if (docSnap.exists()) {
          const firestoreData = docSnap.data();
          userData = {
            uid: user.uid,
            displayName: firestoreData.displayName || userData.displayName,
            email: firestoreData.email || userData.email,
            photoURL: firestoreData.photoURL || userData.photoURL
          };
        } else {
          const newProfileData = {
            displayName: userData.displayName,
            email: userData.email,
            photoURL: userData.photoURL,
          };
          await firestoreSdk.setDoc(userDocRef, newProfileData);
        }
      } catch (docErr) {
        console.warn("Could not fetch manager profile doc during google login, using auth profile:", docErr);
      }

      setCurrentUser(userData);
      setLoading(false);

      if (analytics && analyticsSdk) {
        analyticsSdk.logEvent(analytics, 'login', { method: 'google' });
      }
      return { success: true, error: null };
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      let errorMessage = error.message || 'Google sign-in failed. Please try again.';
      
      if (error?.message?.toLowerCase().includes('cancel') || error?.message?.toLowerCase().includes('closed') || error?.code === 16) {
        errorMessage = 'Sign-in was cancelled.';
      } else if (error.code === 'auth/unauthorized-domain' || errorMessage.includes('unauthorized-domain')) {
        const currentDomain = window.location.hostname;
        errorMessage = `Domain not authorized. Please add BOTH "${currentDomain}" AND "aistudio.google.com" to your Firebase Authorized Domains.`;
      } else if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in popup was closed before completing. Please try again.';
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Multiple popups were opened. Please try again.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const loginWithApple = async (rememberMe: boolean): Promise<AuthResult> => {
    try {
      const { auth, authSdk, db, firestoreSdk, analytics, analyticsSdk } = await initFirebase();
      await authSdk.setPersistence(auth, rememberMe ? authSdk.browserLocalPersistence : authSdk.browserSessionPersistence);
      const provider = new authSdk.OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const result = await authSdk.signInWithPopup(auth, provider);
      const user = result.user;
      
      // Clear any stuck employee/kiosk state upon explicit manager login
      localStorage.removeItem('app_mode');
      localStorage.removeItem('kithhr_employee_session');
      setEmployeeUser(null);

      let userData: AppUser = {
        uid: user.uid,
        displayName: user.displayName || user.email?.split('@')[0] || 'Manager',
        email: user.email,
        photoURL: user.photoURL || null
      };

      try {
        const userDocRef = firestoreSdk.doc(db, 'managers', user.uid);
        const docSnap = await firestoreSdk.getDoc(userDocRef);
        if (docSnap.exists()) {
          const firestoreData = docSnap.data();
          userData = {
            uid: user.uid,
            displayName: firestoreData.displayName || userData.displayName,
            email: firestoreData.email || userData.email,
            photoURL: firestoreData.photoURL || userData.photoURL
          };
        } else {
          const newProfileData = {
            displayName: userData.displayName,
            email: userData.email,
            photoURL: userData.photoURL,
          };
          await firestoreSdk.setDoc(userDocRef, newProfileData);
        }
      } catch (docErr) {
        console.warn("Could not fetch manager profile doc during apple login, using auth profile:", docErr);
      }

      setCurrentUser(userData);
      setLoading(false);

      if (analytics && analyticsSdk) {
        analyticsSdk.logEvent(analytics, 'login', { method: 'apple' });
      }
      return { success: true, error: null };
    } catch (error: any) {
      console.error("Apple sign-in error:", error);
      let errorMessage = error.message || 'Apple sign-in failed. Please try again.';
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in popup was closed before completing. Please try again.';
      }
      return { success: false, error: errorMessage };
    }
  };

  const sendPasswordReset = async (email: string): Promise<AuthResult> => {
    try {
      const { auth, authSdk, functionsSdk } = await initFirebase();
      const cleanEmail = email.trim().toLowerCase();
      if (!cleanEmail || !cleanEmail.includes('@')) {
        return { success: false, error: 'Please enter a valid email address.' };
      }

      // Try calling custom Cloud Function for branded HTML password reset (only if SMTP is actually configured and sent)
      try {
        const functions = functionsSdk.getFunctions(undefined, 'europe-west2');
        const customResetAction = functionsSdk.httpsCallable(functions, 'sendCustomPasswordResetEmail');
        const res: any = await customResetAction({ email: cleanEmail });
        if (res.data?.success && res.data?.method === 'smtp') {
          return { success: true, error: null };
        }
      } catch (fnErr) {
        console.warn("Custom HTML password reset function fallback to Firebase Auth SDK:", fnErr);
      }

      // Fallback directly to Firebase Auth SDK's built-in email delivery
      try {
        const redirectUrl = Capacitor.isNativePlatform() 
          ? 'https://kithhr.co.uk/login' 
          : `${window.location.origin}/login`;

        await authSdk.sendPasswordResetEmail(auth, cleanEmail, {
          url: redirectUrl,
          handleCodeInApp: false
        });
      } catch (actionCodeError: any) {
        console.warn("Password reset with actionCodeSettings failed, falling back to default reset email:", actionCodeError);
        await authSdk.sendPasswordResetEmail(auth, cleanEmail);
      }

      return { success: true, error: null };
    } catch (error: any) {
      console.error("Firebase Password Reset Error:", error);
      let errorMessage = 'An unknown error occurred.';
      switch (error.code) {
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/user-not-found':
          // Security: Return success to prevent email enumeration attacks
          return { success: true, error: null };
        case 'auth/too-many-requests':
          errorMessage = 'Too many attempts. Please wait a few minutes before trying again.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network connection error. Please check your internet connection.';
          break;
        default:
          errorMessage = error.message || 'Failed to send reset link. Please try again later.';
          break;
      }
      return { success: false, error: errorMessage };
    }
  };

  const resendVerificationEmail = async (emailAddress?: string): Promise<AuthResult> => {
      const { auth, authSdk } = await initFirebase();
      if (auth.currentUser) {
          try {
              const actionCodeSettings = {
                url: window.location.origin + '/login',
                handleCodeInApp: false
              };
              try {
                await authSdk.sendEmailVerification(auth.currentUser, actionCodeSettings);
              } catch (e) {
                await authSdk.sendEmailVerification(auth.currentUser);
              }
              return { success: true, error: null };
          } catch (e: any) {
              return { success: false, error: e.message || "Failed to resend verification email. Please wait a moment." };
          }
      }
      return { success: false, error: "Session expired or user not signed in. Please try signing in again to receive a new link." };
  };

  const clearAppStorage = async () => {
    try {
      const savedTheme = localStorage.getItem('theme') || 'dark';
      localStorage.clear();
      sessionStorage.clear();
      localStorage.setItem('theme', savedTheme);
      if (window.indexedDB && window.indexedDB.databases) {
        const dbs = await window.indexedDB.databases();
        for (const dbInfo of dbs) {
          if (dbInfo.name) window.indexedDB.deleteDatabase(dbInfo.name);
        }
      }
    } catch (e) {
      console.warn("Error clearing app storage:", e);
    }
  };

  const logout = async () => {
    const { auth, authSdk, analytics, analyticsSdk } = await initFirebase();
    if (analytics && analyticsSdk) {
      analyticsSdk.logEvent(analytics, 'logout');
    }
    
    setEmployeeUser(null);
    setCurrentUser(null);
    
    await authSdk.signOut(auth);
    await clearAppStorage();
    window.location.href = '/login/';
  };

  const logoutEmployee = async () => {
    setEmployeeUser(null);
    localStorage.removeItem('kithhr_employee_session');
    window.location.href = '/';
  };

  const updateUserProfile = async (data: { displayName?: string; email?: string; photoURL?: string }): Promise<{ success: boolean; error: string | null }> => {
    const { auth, authSdk, db, firestoreSdk } = await initFirebase();
    const user = auth.currentUser;
    if (user) {
      try {
        if (data.displayName || data.photoURL) {
          await authSdk.updateProfile(user, { 
            ...(data.displayName && { displayName: data.displayName }),
            ...(data.photoURL && { photoURL: data.photoURL })
          });
        }
        if (data.email && data.email !== user.email) {
          await authSdk.verifyBeforeUpdateEmail(user, data.email);
          throw new Error("verification-sent");
        }
        
        const userDocRef = firestoreSdk.doc(db, 'managers', user.uid);
        await firestoreSdk.updateDoc(userDocRef, data);
        
        setCurrentUser(prevUser => {
          if (!prevUser) return null;
          return {
            ...prevUser,
            ...(data.displayName && { displayName: data.displayName }),
            ...(data.email && { email: data.email }),
            ...(data.photoURL && { photoURL: data.photoURL }),
          };
        });

        return { success: true, error: null };
      } catch (error: any) {
        if (error.message === 'verification-sent') {
          return { success: true, error: 'A verification link has been sent to your new email. Please verify it before the change takes effect. Other profile details were saved.' };
        }
        let errorMessage = 'Failed to update profile.';
        if (error.code === 'auth/requires-recent-login') {
          errorMessage = 'This is a sensitive operation. Please sign out and sign back in to update your email.';
        } else if (error.code === 'auth/email-already-in-use') {
          errorMessage = 'This email address is already in use by another account.';
        }
        return { success: false, error: errorMessage };
      }
    }
    return { success: false, error: 'No authenticated user found.' };
  };

  const deleteUserAccount = async (): Promise<{ success: boolean; error: string | null }> => {
    const { auth, authSdk, functionsSdk } = await initFirebase();
    if (!auth.currentUser) {
      return { success: false, error: 'No user is currently signed in.' };
    }

    try {
      console.log("Starting account deletion process via Cloud Function...");
      const functions = functionsSdk.getFunctions(undefined, 'europe-west2');
      const deleteAction = functionsSdk.httpsCallable(functions, 'deleteManagerAccount');
      await deleteAction();

      console.log("Account deleted successfully from backend. Clearing session...");
      setCurrentUser(null);
      setEmployeeUser(null);
      await authSdk.signOut(auth).catch(() => {});
      await clearAppStorage();
      
      return { success: true, error: null };
    } catch (error: any) {
      console.error('CRITICAL: Error during account deletion:', error);
      let errorMessage = error.message || 'Failed to delete account. Please try again later.';
      if (error.code === 'functions/unauthenticated' || errorMessage.includes('unauthenticated')) {
        errorMessage = 'Your session has expired. Please sign out and sign back in before deleting your account.';
      }
      return { success: false, error: errorMessage };
    }
  };

  const saveFcmToken = async (token: string): Promise<void> => {
    try {
      const { auth, db, firestoreSdk } = await initFirebase();
      if (auth.currentUser && !auth.currentUser.isAnonymous) {
        const userDocRef = firestoreSdk.doc(db, 'managers', auth.currentUser.uid);
        const profileDocRef = firestoreSdk.doc(db, 'managers', auth.currentUser.uid, 'company', 'profile');
        const tokenData = {
          fcmToken: token,
          fcmTokenUpdatedAt: new Date().toISOString()
        };
        await firestoreSdk.setDoc(userDocRef, tokenData, { merge: true });
        await firestoreSdk.setDoc(profileDocRef, tokenData, { merge: true });
        console.log("FCM token saved for manager");
      }
      if (employeeUser) {
        const employeeDocRef = firestoreSdk.doc(db, 'managers', employeeUser.managerId, 'employees', employeeUser.id);
        await firestoreSdk.setDoc(employeeDocRef, {
          fcmToken: token,
          fcmTokenUpdatedAt: new Date().toISOString()
        }, { merge: true });
        console.log("FCM token saved for employee");
      }
    } catch (error) {
      console.error("Error saving FCM token:", error);
    }
  };

  const value = {
    currentUser,
    employeeUser,
    isAuthenticated: !!currentUser,
    isEmployeeAuthenticated: !!employeeUser,
    loading,
    login,
    loginAsEmployee,
    register,
    loginWithGoogle,
    loginWithApple,
    logout,
    logoutEmployee,
    updateUserProfile,
    sendPasswordReset,
    deleteUserAccount,
    resendVerificationEmail,
    pairKioskDevice,
    saveFcmToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
